/**
 * The book: searching, filtering, sorting and totalling a company's accounts.
 *
 * Every derived value here already exists somewhere — `overdueAgeing`,
 * `getFollowUpCategory`, `getCustomerPaymentRank`, `matchesSearch`,
 * `matchesSettlement` — and this file arranges them rather than re-deciding
 * anything. That matters: the ageing rule in particular is subtle (a credit
 * note 135 days old is not money 135 days overdue) and was got right once.
 *
 * Pure, so the list's behaviour is testable without a database. What it is
 * **not** is a security boundary: which accounts reach this function is decided
 * by `can_see_customer()` in SQL, and a filter here is a view concern. If this
 * file were deleted, a restricted member would still receive only their own
 * accounts from the server.
 */
import type { AgeingBucket, Customer, PaymentRank, SettlementFilter } from './customer.js';
import { hasOutstanding, matchesSettlement } from './customer.js';
import { getCustomerPaymentRank, getFollowUpCategory, isBadDebt, type FollowUpCategory } from './followUp.js';
import { overdueAgeing, type OverdueAgeing } from './money.js';
import { matchesSearch } from './search.js';

/* --------------------------------- reading -------------------------------- */

/** One row of the book, with everything the list shows worked out once. */
export interface BookRow {
    customer: Customer;
    overdue: OverdueAgeing;
    followUp: FollowUpCategory;
    rank: PaymentRank;
    badDebt: boolean;
    settled: boolean;
    /** Negative when the follow-up is in the past. Null when none is set. */
    daysToFollowUp: number | null;
}

const midnight = (value: Date): number => {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

const DAY = 86_400_000;

export function bookRow(customer: Customer, today: Date = new Date()): BookRow {
    const followUp = getFollowUpCategory(customer, today);
    let daysToFollowUp: number | null = null;
    if (customer.followUpDate) {
        const due = new Date(customer.followUpDate);
        if (!isNaN(due.getTime())) {
            daysToFollowUp = Math.round((midnight(due) - midnight(today)) / DAY);
        }
    }

    return {
        customer,
        overdue: overdueAgeing(customer),
        followUp,
        rank: getCustomerPaymentRank(customer),
        badDebt: isBadDebt(customer),
        settled: !hasOutstanding(customer),
        daysToFollowUp,
    };
}

/* -------------------------------- filtering ------------------------------- */

/**
 * An ageing window, or one bucket.
 *
 * The windows are what the Book's own filter has always offered. The four
 * `bucket*` values were added with the ageing report, so that pressing a row of
 * it opens exactly the accounts that row counted — one definition, in
 * `book_matches()`, used by the report's number, the report's link and the
 * Book's list.
 */
export type AgeingFilter =
    | 'any' | 'over45' | 'over90' | 'over135'
    | 'bucket1' | 'bucket2' | 'bucket3' | 'bucket4';

/**
 * The ageing bands, in order, each with the filter value that opens it.
 *
 * The keys are `AGEING_BUCKETS` from `customer.ts` — the ones a sheet's columns
 * map to — so there is one set of bands in the product and this only says how
 * to name them and how to ask the Book for one.
 */
export const AGEING_BANDS: readonly { bucket: AgeingBucket; label: string; filter: AgeingFilter }[] = [
    { bucket: '1-45', label: '1 to 45 days', filter: 'bucket1' },
    { bucket: '46-90', label: '46 to 90 days', filter: 'bucket2' },
    { bucket: '91-135', label: '91 to 135 days', filter: 'bucket3' },
    { bucket: '>135', label: 'Over 135 days', filter: 'bucket4' },
];
export type FollowUpFilter = FollowUpCategory | 'any';
export type RankFilter = PaymentRank | 'any';
/** A member id, or one of the two answers that are not a member. */
export type OwnerFilter = string;

export interface BookFilters {
    search: string;
    settlement: SettlementFilter;
    followUp: FollowUpFilter;
    ageing: AgeingFilter;
    /** `any`, `unassigned`, `mine`, or a member id. */
    owner: OwnerFilter;
    rank: RankFilter;
    urgentOnly: boolean;
}

/**
 * The book's resting state: everything that is actually work.
 *
 * `withDues` rather than `all`, because most customers owe nothing. They are
 * real customers and every word of their history matters, but mixed into a
 * worklist they bury the few hundred accounts that are work — "no follow-up
 * set" once read in the thousands when the true answer was a few hundred.
 */
export const DEFAULT_FILTERS: BookFilters = {
    search: '',
    settlement: 'withDues',
    followUp: 'any',
    ageing: 'any',
    owner: 'any',
    rank: 'any',
    urgentOnly: false,
};

export const filtersAreDefault = (filters: BookFilters): boolean =>
    filters.search.trim() === ''
    && filters.settlement === DEFAULT_FILTERS.settlement
    && filters.followUp === 'any'
    && filters.ageing === 'any'
    && filters.owner === 'any'
    && filters.rank === 'any'
    && !filters.urgentOnly;

/** How many filters are narrowing the list, for a "clear filters" affordance. */
export const activeFilterCount = (filters: BookFilters): number =>
    (filters.search.trim() ? 1 : 0)
    + (filters.settlement !== DEFAULT_FILTERS.settlement ? 1 : 0)
    + (filters.followUp !== 'any' ? 1 : 0)
    + (filters.ageing !== 'any' ? 1 : 0)
    + (filters.owner !== 'any' ? 1 : 0)
    + (filters.rank !== 'any' ? 1 : 0)
    + (filters.urgentOnly ? 1 : 0);

/**
 * What a search looks at.
 *
 * The company's own code is included because it is what people read off a
 * sheet; the imported owner code because "whose accounts are these" is a
 * question asked of the book before anybody has been assigned.
 */
const searchableParts = (c: Customer): (string | null | undefined)[] => [
    c.company, c.sourceKey, c.contactPerson, c.contactNumber,
    c.email, c.city, c.state, c.taxId, c.category, c.importedOwnerCode,
];

export interface OwnerContext {
    /** The member looking at the book, so `mine` means something. */
    memberId?: string;
}

export function matchesBookFilters(
    row: BookRow,
    filters: BookFilters,
    context: OwnerContext = {},
): boolean {
    const c = row.customer;

    if (!matchesSettlement(c, filters.settlement)) return false;
    if (filters.urgentOnly && !c.isUrgent) return false;
    if (filters.followUp !== 'any' && row.followUp !== filters.followUp) return false;
    if (filters.rank !== 'any' && row.rank !== filters.rank) return false;

    if (filters.ageing !== 'any') {
        const amount = filters.ageing === 'over45' ? row.overdue.over45
            : filters.ageing === 'over90' ? row.overdue.over90
                : row.overdue.over135;
        if (amount <= 0) return false;
    }

    if (filters.owner !== 'any') {
        const owner = c.ownerMemberId as string | undefined;
        const collector = c.collectorMemberId as string | undefined;
        if (filters.owner === 'unassigned') {
            if (owner || collector) return false;
        } else if (filters.owner === 'mine') {
            const me = context.memberId;
            if (!me || (owner !== me && collector !== me)) return false;
        } else if (owner !== filters.owner && collector !== filters.owner) {
            return false;
        }
    }

    return matchesSearch(searchableParts(c), filters.search);
}

export function filterBook(
    rows: readonly BookRow[],
    filters: BookFilters,
    context: OwnerContext = {},
): BookRow[] {
    return rows.filter(row => matchesBookFilters(row, filters, context));
}

/**
 * The filters, read out of the address bar.
 *
 * Anything unrecognised falls back to the default rather than being trusted: a
 * pasted link is somebody else's, and a filter value is not validated anywhere
 * else.
 *
 * It lives here rather than on the screen because it is the other half of
 * `BookFilters` — and because the worklist builds links the book has to parse,
 * so a test can prove a card opens exactly the question it counted.
 */
export function filtersFromParams(params: URLSearchParams): BookFilters {
    const oneOf = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
        allowed.includes((value ?? '') as T) ? (value as T) : fallback;

    return {
        search: params.get('search') ?? '',
        settlement: oneOf(params.get('settlement'), ['withDues', 'settled', 'all'] as const, 'withDues'),
        followUp: oneOf(
            params.get('followUp'),
            ['any', 'today', 'future', 'overdue', 'no_follow_up', 'completed'] as const,
            'any',
        ),
        ageing: oneOf(
            params.get('ageing'),
            ['any', 'over45', 'over90', 'over135',
             'bucket1', 'bucket2', 'bucket3', 'bucket4'] as const,
            'any',
        ),
        owner: params.get('owner') ?? 'any',
        rank: oneOf(params.get('rank'), ['any', 'Good', 'Late', 'Bad'] as const, 'any'),
        urgentOnly: params.get('urgentOnly') === 'true',
    };
}

/* --------------------------------- sorting -------------------------------- */

export type BookSort =
    | 'amount-desc'
    | 'amount-asc'
    | 'oldest-first'
    | 'follow-up'
    | 'name'
    | 'recently-updated';

export const SORT_LABELS: Record<BookSort, string> = {
    'amount-desc': 'Largest amount',
    'amount-asc': 'Smallest amount',
    'oldest-first': 'Oldest money',
    'follow-up': 'Follow-up date',
    'name': 'Name',
    'recently-updated': 'Recently updated',
};

export const DEFAULT_SORT: BookSort = 'amount-desc';

const amountOf = (row: BookRow): number => Math.abs(Number(row.customer.total) || 0);

const time = (value: Date | string | null | undefined): number => {
    if (!value) return 0;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? 0 : d.getTime();
};

/**
 * Sorted, stably, and never mutating what it was given.
 *
 * "Follow-up date" puts the accounts with no date **last** rather than first.
 * A blank sorts before everything if it is treated as zero, which put every
 * untouched account at the top of the one list meant to be worked in order.
 */
export function sortBook(rows: readonly BookRow[], sort: BookSort): BookRow[] {
    const sorted = [...rows];
    const byName = (a: BookRow, b: BookRow) =>
        a.customer.company.localeCompare(b.customer.company, undefined, { sensitivity: 'base' });

    switch (sort) {
        case 'amount-desc':
            sorted.sort((a, b) => amountOf(b) - amountOf(a) || byName(a, b));
            break;
        case 'amount-asc':
            sorted.sort((a, b) => amountOf(a) - amountOf(b) || byName(a, b));
            break;
        case 'oldest-first':
            // The oldest money first: what has been outstanding longest is what
            // is least likely to arrive on its own.
            sorted.sort((a, b) =>
                b.overdue.over135 - a.overdue.over135
                || b.overdue.over90 - a.overdue.over90
                || b.overdue.over45 - a.overdue.over45
                || byName(a, b));
            break;
        case 'follow-up':
            sorted.sort((a, b) => {
                const av = a.daysToFollowUp;
                const bv = b.daysToFollowUp;
                if (av === null && bv === null) return byName(a, b);
                if (av === null) return 1;
                if (bv === null) return -1;
                return av - bv || byName(a, b);
            });
            break;
        case 'name':
            sorted.sort(byName);
            break;
        case 'recently-updated':
            sorted.sort((a, b) =>
                time(b.customer.lastFollowUpOn) - time(a.customer.lastFollowUpOn) || byName(a, b));
            break;
    }
    return sorted;
}

/* --------------------------------- totals --------------------------------- */

export interface BookTotals {
    count: number;
    /** Net receivable across the rows shown. Credits count down. */
    outstanding: number;
    over90: number;
    dueToday: number;
    overdue: number;
    unassigned: number;
}

/**
 * What the rows on screen add up to.
 *
 * Over the **filtered** rows on purpose: a total that ignores the filter
 * answers a question nobody asked, and invites somebody to read a company-wide
 * figure off a screen showing one collector's accounts.
 */
export function bookTotals(rows: readonly BookRow[]): BookTotals {
    let outstanding = 0;
    let over90 = 0;
    let dueToday = 0;
    let overdue = 0;
    let unassigned = 0;

    for (const row of rows) {
        const amount = Math.abs(Number(row.customer.total) || 0);
        outstanding += row.customer.totalType === 'Cr' ? -amount : amount;
        over90 += row.overdue.over90;
        if (row.followUp === 'today') dueToday += 1;
        if (row.followUp === 'overdue') overdue += 1;
        if (!row.customer.ownerMemberId && !row.customer.collectorMemberId) unassigned += 1;
    }

    return { count: rows.length, outstanding, over90, dueToday, overdue, unassigned };
}

/**
 * The whole view, in one call: derive, filter, sort, total.
 *
 * One function so a screen cannot do the three steps in a different order from
 * the tests — totalling before filtering, in particular, is an easy mistake and
 * produces a number that looks plausible.
 */
export function buildBook(
    customers: readonly Customer[],
    filters: BookFilters,
    sort: BookSort,
    context: OwnerContext = {},
    today: Date = new Date(),
): { rows: BookRow[]; totals: BookTotals; totalAvailable: number } {
    const all = customers.map(c => bookRow(c, today));
    const matching = filterBook(all, filters, context);
    return {
        rows: sortBook(matching, sort),
        totals: bookTotals(matching),
        totalAvailable: all.length,
    };
}
